/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {LocalDate} from 'spine-web/proto/spine/time/time_pb';
import i18n from '@/shell/i18n/i18n';

function t(relativeKey: string | number): string {
  return i18n.t('lserv-app.shell.model.spine.time.LocalDateExt.' + relativeKey) as string;
}

/**
 * A class that "extends" (using composition) `LocalDate` with respective
 * entity-specific operations.
 */
export default class LocalDateExt {
  /**
   * Creates an instance of `LocalDateExt` that wraps the provided `LocalDate`.
   */
  static of(localDate: LocalDate): LocalDateExt {
    if (!localDate) {
      throw new Error('localDate value should be present: ' + localDate);
    }
    return new LocalDateExt(localDate);
  }

  /**
   * Creates a `LocalDateExt` instance that represents the today date.
   */
  static today(): LocalDateExt {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (testingOptions.today) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return testingOptions.today;
    }
    const now = new Date();
    return this.fromDate(now);
  }

  /**
   * Creates an instance from the specified JavaScript `Date` object, by interpreting it in
   * the local timezone. The time fields of the passed `Date` are ignored.
   */
  static fromDate(date: Date): LocalDateExt {
    const localDate = new LocalDate()
        .setYear(date.getFullYear())
        .setMonth(date.getMonth() + 1)
        .setDay(date.getDate());
    return new LocalDateExt(localDate);
  }

  private constructor(private readonly localDate: LocalDate) {
    this.localDate = localDate;
  }

  /**
   * The `LocalDate` object that corresponds to this instance.
   */
  get value(): LocalDate {
    return this.localDate;
  }

  /**
   * @param otherDateExt  A date to relate this one with.
   * @return  A number of days from the day represented by this instance to the day represented by
   *          the `otherDateExt` param. This works as an arithmetic subtraction (otherDateExt - this),
   *          for example:
   *          - `date_10_may_2021.until([date_11_may_2021])` returns `1`;
   *          - `date_1_jan_2021.until([date_30_dec_2020])` returns `-2`.
   *
   *          Note that this method always returns a round amount of days, and interprets
   *          shorter/longer days that happen during the daylight saving time transition days as
   *          exactly 1 day (regardless of their actual duration of, for example 23 or 25 hours).
   */
  until(otherDateExt: LocalDateExt): number {
    const thisDate = this.toDate();
    const otherDate = otherDateExt.toDate();
    const diffMillis = otherDate.getTime() - thisDate.getTime();
    const daysUntilOtherDate = Math.round(diffMillis / (1000 * 60 * 60 * 24));
    return daysUntilOtherDate;
  }

  /**
   * Adds the specified number of days to the date represented by the current instance, and returns
   * a corresponding new `LocalDateExt` instance (this instance is not affected).
   *
   * @param days the number of days to add to this date. E.g. `1` will refer to the next day, and
   *             `-1` will refer to the previous day.
   */
  addDays(days: number): LocalDateExt {
    const newDate = this.toDate();
    newDate.setDate(newDate.getDate() + days);
    return LocalDateExt.fromDate(newDate);
  }

  /**
   * Adds the specified number of months to the date represented by the current instance, and
   * returns a corresponding new `LocalDateExt` instance (this instance is not affected).
   *
   * @param months The number of months to add to this date. E.g. `1` will refer to the next month,
   *               and `-1` will refer to the previous one. The day of month will typically remain
   *               the same as a result (if there's such a day in the target month). More precisely,
   *               this method uses the JavaScript `Date` object's `getMonth()`/`setMonth()`
   *               methods, which identify the respective behavior in any edge cases.
   */
  addMonths(months: number): LocalDateExt {
    const newDate = this.toDate();
    newDate.setMonth(newDate.getMonth() + months);
    return LocalDateExt.fromDate(newDate);
  }

  /**
   * @return the `Date` object representation of the associated `LocalDate`, which implies the time
   *         fields to be equal to 0. The returned `Date` is calculated by interpreting `LocalDate`
   *         in the local time zone. It is a new `Date` instance that is not itself a part of this
   *         `LocalDateExt`'s internal state.
   */
  toDate(): Date {
    const date = this.localDate;
    const year = date.getYear();
    const month = date.getMonth() - 1;
    const day = date.getDay();
    return new Date(year, month, day);
  }

  /**
   * @return a string representation of this date formatted with the "yyyy-mm-dd" format,
   *          e.g. a value of "2021-01-02" represents January 2, 2021.
   */
  toYyyyMmDd(): string {
    const year = this.value.getYear();
    const month = this.value.getMonth();
    const day = this.value.getDay();
    return `${year}-${this.padNumber(month)}-${this.padNumber(day)}`;
  }

  /**
   * Represents this date as a human readable text with the application-specific format that
   * includes all components of the date (year, month, and day).
   *
   * @param allowCloseDays  setting to true allows the method to return the name of days that are
   *                          close to today instead of returning the date string: yesterday,
   *                          today, tomorrow.
   * @param includeYear     setting to `false` removes the year from the formatted text.
   * @param includeMonth    setting to `false` removes the month from the formatted text.
   * @param includeDay      setting to `false` removes the day from the formatted text.
   */
  format(allowCloseDays = false,
         includeYear = true,
         includeMonth = true,
         includeDay = true
  ): string {
    const daysAfterToday = LocalDateExt.today().until(this);
    if (allowCloseDays) {
      const closeDayName = LocalDateExt.getCloseDayName(daysAfterToday);
      if (closeDayName !== undefined) {
        return closeDayName;
      }
   }
    const date = this.toDate();
    return date.toLocaleDateString(
        i18n.locale,
        {
          day: includeDay ? 'numeric' : undefined,
          month: includeMonth ? 'short' : undefined,
          year: includeYear ? 'numeric' : undefined
        }
    );
  }

  /**
   * Represents this date as a human-readable text with the application-specific format that
   * depends upon the closeness of the date to the current one.
   */
  formatAsRelativeDate(): string {
    const today = LocalDateExt.today();
    const daysAfterToday = today.until(this);
    const closeDayName = LocalDateExt.getCloseDayName(daysAfterToday);
    if (closeDayName !== undefined) {
      return closeDayName;
    }

    // use the full format for distant dates or dates from the other year
    const almostHalfAYearDays = 180;
    if (today.localDate.getYear() !== this.localDate.getYear() ||
        Math.abs(daysAfterToday) >= almostHalfAYearDays
    ) {
      return this.format();
    }

    // otherwise, use a shortened format without a year
    const date = this.toDate();
    return date.toLocaleDateString(
        i18n.locale,
        {day: 'numeric', month: 'long'});
  }

  /**
   * @return a technical (non user presentable) string representation of this object.
   */
  toString(): string {
    const ld = this.localDate;
    return `LocalDateExt[year=${ld.getYear()}; month=${ld.getMonth()}; day=${ld.getDay()}]`;
  }

  /**
   * Checks whether this instance represents the same day as another `LocalDateExt` instance.
   *
   * @param other an instance to compare this one with.
   * @return `true`, if the other instance refers to the same date, and `false` otherwise.
   */
  equals(other: LocalDateExt): boolean {
    return (
        this.value.getYear() === other.value.getYear() &&
        this.value.getMonth() === other.value.getMonth() &&
        this.value.getDay() === other.value.getDay()
    );
  }

  /**
   * Returns a human-readable day name in the current locale of a given relative day, if it is
   * considered to be a "close" day (yesterday, today, or tomorrow).
   *
   * @param daysAfterToday  a relative day whose name should be returned (number of days that this
   *                        day is ahead from today). E.g., 0 means today, -1 means yesterday,
   *                        and 1 means tomorrow
   * @return a name of the given day in the current locale, if it is yesterday, today, or tomorrow,
   *         or `undefined` if it is any other day
   */
  private static getCloseDayName(daysAfterToday: number): string | undefined {
    switch (daysAfterToday) {
      case -1:
        return t('yesterday');
      case 0:
        return t('today');
      case 1:
        return t('tomorrow');
    }
    return undefined;
  }

  private padNumber(n: number) {
    let str = '' + n;
    if (str.length < 2) {
      str = '0' + str;
    }
    return str;
  }
}

/**
 * Options that can be overridden to override the default logic of `LocalDateExt`
 * for testing purposes.
 */
interface LocalDateExtOptions {

  /**
   * Allows overriding the value returned by `LocalDateExt.today()`.
   */
  today?: LocalDateExt;
}

/**
 * Can be used to override some of the default logic for testing purposes.
 */
export const testingOptions: LocalDateExtOptions = {
  today: undefined
};
